<template>
  <aside class="sidebar" :class="{ 'is-open': isOpen }">

    <!-- Mobile close button -->
    <button class="sb-close" @click="$emit('close')" aria-label="Close menu">✕</button>

    <!-- Header / Brand -->
    <div class="sb-header">
      <router-link :to="{ path: '/' }" class="sb-brand-link" @click.native="$emit('close')">
        <div class="sb-title">
          Online Library for <span class="sb-accent">Green Inclusive Finance</span>
        </div>
        <div class="sb-sub">HEDERA Sustainable Solutions</div>
      </router-link>
    </div>

    <!-- Database section -->
    <div class="sb-section">
      <p class="sb-label">Database</p>
      <ul class="sb-list">
        <li v-for="item in paperList" :key="item.name">
          <a
            :class="['sb-item', { 'is-active': tabActive(item.name) }]"
            :href="'/#/papers/' + item.name"
            @click="$emit('close')"
          >
            <i :class="item.icon"></i>
            {{ item.title }}
          </a>
        </li>
      </ul>
    </div>

    <!-- Logged-in features -->
    <div class="sb-section" v-if="isLoggedIn() && loggedInFeatures.length">
      <ul class="sb-list">
        <li v-for="item in loggedInFeatures" :key="item.name">
          <a
            :class="['sb-item', { 'is-active': tabActive(item.name) }]"
            :href="'/#/papers/' + item.name"
            @click="$emit('close')"
          >
            <i :class="item.icon"></i>
            {{ item.title }}
          </a>
        </li>
      </ul>
    </div>

    <!-- Admin section -->
    <div class="sb-section" v-if="isAdmin()">
      <p class="sb-label">Admin</p>
      <ul class="sb-list">
        <li v-for="item in userList" :key="item.name">
          <a
            :class="['sb-item', { 'is-active': tabActive(item.name) }]"
            :href="'/#/papers/' + item.name"
            @click="$emit('close')"
          >
            <i :class="item.icon"></i>
            {{ item.title }}
          </a>
        </li>
      </ul>
    </div>

    <!-- User area -->
    <div class="sb-section">
      <p class="sb-label">User Area</p>
      <ul class="sb-list">
        <li v-if="isLoggedIn()">
          <a class="sb-item" @click="onLogout()">
            <i class="fas fa-sign-out-alt"></i>
            Logout
          </a>
        </li>
        <li v-if="!isLoggedIn()">
          <router-link to="/login" class="sb-item" @click.native="$emit('close')">
            <i class="fas fa-sign-in-alt"></i>
            Login
          </router-link>
        </li>
      </ul>
    </div>

    <div class="sb-spacer"></div>

    <!-- Footer -->
    <a class="sb-footer" href="https://connect.hedera.online/" target="_blank" rel="noopener">
      <span class="sb-powered">Powered by</span>
      <img :src="image" alt="HEDERA" class="sb-logo" />
    </a>

  </aside>
</template>

<script>
import image from '../../assets/HEDERA-blatt.png'

export default {
  name: 'Sidebar',
  props: ['menuActive'],
  computed: {
    isOpen () {
      return this.$parent.sidebarOpen
    }
  },
  data () {
    return {
      image,
      paperList: [
        { name: 'search', title: 'Search', icon: 'fa fa-search' }
      ],
      userList: [
        { name: 'users', title: 'Users', icon: 'fa fa-user' },
        { name: 'add-paper', title: 'Add Paper', icon: 'fa fa-plus' }
      ],
      loggedInFeatures: []
    }
  },
  methods: {
    isLoggedIn () {
      return localStorage.getItem('app-token') !== null
    },
    tabActive (tab) {
      return this.menuActive === tab
    },
    onLogout () {
      localStorage.removeItem('app-token')
      location.reload()
    },
    isAdmin () {
      const userItem = JSON.parse(localStorage.getItem('app-token'))
      if (userItem === null) return false
      return userItem['loginUser']['userType'] === 'admin'
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap');

/* ── Shell ── */
.sidebar {
  position: fixed;
  top: 0;
  left: 0;
  width: 220px;
  height: 100vh;
  background-color: #1b3d2f;
  display: flex;
  flex-direction: column;
  font-family: 'Poppins', sans-serif;
  overflow-y: auto;
  overflow-x: hidden;
  z-index: 200;
  transition: transform 0.25s ease;
}

/* ── Mobile: hidden by default, slides in ── */
@media (max-width: 768px) {
  .sidebar {
    transform: translateX(-100%);
  }
  .sidebar.is-open {
    transform: translateX(0);
  }
}

/* ── Mobile close button ── */
.sb-close {
  display: none;
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(255,255,255,0.08);
  border: none;
  color: #c8dbd3;
  font-size: 14px;
  width: 28px;
  height: 28px;
  border-radius: 4px;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

@media (max-width: 768px) {
  .sb-close {
    display: flex;
  }
}

/* ── Header ── */
.sb-header {
  padding: 18px 16px 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.sb-brand-link {
  text-decoration: none;
  display: block;
}

.sb-title {
  color: #f0ede6;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.35;
  letter-spacing: -0.01em;
  padding-right: 30px; /* avoid overlap with close button on mobile */
}

.sb-accent {
  color: #74c69d;
}

.sb-sub {
  color: #7aaa8d;
  font-size: 9px;
  font-weight: 500;
  margin-top: 4px;
}

/* ── Sections ── */
.sb-section {
  padding: 14px 10px 4px;
  flex-shrink: 0;
}

.sb-label {
  color: #7aaa8d;
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 0 8px;
  margin-bottom: 4px;
}

.sb-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

/* ── Nav items ── */
.sb-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  border-radius: 6px;
  color: #c8dbd3;
  font-size: 12px;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.sb-item i {
  font-size: 11px;
  width: 14px;
  text-align: center;
  opacity: 0.75;
  flex-shrink: 0;
}

.sb-item:hover {
  background-color: rgba(255, 255, 255, 0.08);
  color: #ffffff;
}

.sb-item.is-active {
  background-color: #2d6a4f;
  color: #ffffff;
}

.sb-item.is-active i {
  opacity: 1;
}

/* ── Spacer ── */
.sb-spacer {
  flex: 1;
  min-height: 24px;
}

/* ── Footer ── */
.sb-footer {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 12px 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  text-decoration: none;
  flex-shrink: 0;
  transition: background 0.15s;
}

.sb-footer:hover {
  background-color: rgba(255, 255, 255, 0.05);
}

.sb-powered {
  color: #7aaa8d;
  font-size: 10px;
  font-weight: 500;
  white-space: nowrap;
}

.sb-logo {
  height: 22px;
  width: auto;
  display: block;
  object-fit: contain;
}
</style>



// WEBPACK FOOTER //
// src/components/Admin/Menu.vue